/**
 * The bell, its panel, and the toast that arrives while you are working.
 *
 * Three decisions, all of them made against the sketch rather than in here:
 *
 * **A dropdown, not a drawer.** This app already has a right-hand drawer and it
 * belongs to the assistant. Two drawers opening from the same edge and meaning
 * different things is one too many, and a list you skim for five seconds does
 * not need the full height of the window.
 *
 * **A count, not a dot.** "Three people shared things while you were out" is
 * worth knowing before you click, and the number is already in the response.
 * Not red: red is for what is wrong, and a colleague sharing a chart is not.
 *
 * **Clicking lands you on the thing.** A notification that only marks itself
 * read is a chore, not a message - so each one carries the query string that
 * opens what it is about, and uses the same restore path a shared link does.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  markRead,
  newestArrival,
  notifications,
  notificationsAvailable,
  startNotificationPolling,
  unreadCount,
} from '../../state/notifications.ts';
import { restoreFromUrl } from '../../state/url-sync.ts';
import type { Notification } from '../../api/types.ts';

/** How long an arrival stays on screen before it goes quiet. */
const TOAST_MS = 6000;

const BELL = html`<svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M12 3a5.6 5.6 0 0 0-5.6 5.6c0 4-1.4 5.3-1.4 5.3h14s-1.4-1.3-1.4-5.3A5.6 5.6 0 0 0 12 3Z"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linejoin="round"
  />
  <path d="M10.2 17.4a2 2 0 0 0 3.6 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
</svg>`;

/** "4 min ago", "Yesterday" - the resolution people actually read. */
function ago(iso: string, now = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((now - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Today / Yesterday / Earlier - one heading, where the day changes. */
function bucket(iso: string, now = new Date()): string {
  const when = new Date(iso);
  const day = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(when)) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return 'Earlier';
}

/** Initials for the disc, from whoever did it. */
function initials(name: string | null | undefined): string {
  if (!name) return '·';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

@customElement('notification-bell')
export class NotificationBell extends LightElement {
  private store = new StoreController(this, [
    notifications,
    unreadCount,
    notificationsAvailable,
    newestArrival,
  ]);

  @state() private open = false;
  @state() private toast: Notification | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private lastToasted: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    startNotificationPolling();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.toastTimer) clearTimeout(this.toastTimer);
    super.disconnectedCallback();
  }

  /** A click anywhere else closes the panel, the way every menu here does. */
  private onDocumentClick = (e: MouseEvent): void => {
    if (!this.open) return;
    if (e.composedPath().includes(this)) return;
    this.open = false;
  };

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) this.open = false;
  };

  /** One toast at a time: a second arrival replaces the first rather than
   *  stacking a pile of them over the page. */
  protected override updated(): void {
    const arrival = newestArrival.value;
    if (!arrival || arrival.id === this.lastToasted) return;
    this.lastToasted = arrival.id;
    this.toast = arrival;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toast = null;
    }, TOAST_MS);
  }

  /** Open what it is about, and mark it read on the way. */
  private async openNotification(n: Notification): Promise<void> {
    this.open = false;
    this.toast = null;
    if (!n.read) void markRead([n.id]);
    if (!n.link) return;
    await restoreFromUrl(n.link);
  }

  private renderPanel(): TemplateResult {
    const items = notifications.value;
    let lastBucket = '';
    return html`
      <div class="note-panel" role="dialog" aria-label="Notifications">
        <div class="note-panel-bar">
          <span class="note-panel-name">Notifications</span>
          ${unreadCount.value
            ? html`<button class="note-act" @click=${() => void markRead()}>Mark all read</button>`
            : nothing}
        </div>
        <div class="note-list scroll-thin">
          ${items.length
            ? items.map((n) => {
                const group = bucket(n.created_at);
                const heading = group === lastBucket ? nothing : html`<div class="note-day">${group}</div>`;
                lastBucket = group;
                return html`${heading}
                  <button
                    class="note-row ${n.read ? '' : 'unread'}"
                    @click=${() => void this.openNotification(n)}
                  >
                    <span class="note-who ${n.actor_name ? '' : 'system'}">
                      ${initials(n.actor_name)}
                    </span>
                    <span class="note-text">
                      ${n.title}
                      <span class="note-when">${ago(n.created_at)}</span>
                    </span>
                  </button>`;
              })
            : html`<p class="note-empty">
                Nothing yet. When somebody shares a chart or a dashboard with you,
                it will be here.
              </p>`}
        </div>
      </div>
    `;
  }

  override render(): TemplateResult | typeof nothing {
    // No database, no inbox - and no bell, rather than one that can never fill.
    if (!notificationsAvailable.value) return nothing;
    const unread = unreadCount.value;
    return html`
      <button
        class="note-bell ${this.open ? 'open' : ''}"
        title="Notifications"
        aria-label=${unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded=${this.open}
        @click=${() => (this.open = !this.open)}
      >
        ${BELL}
        ${unread
          ? html`<span class="note-count">${unread > 9 ? '9+' : unread}</span>`
          : nothing}
      </button>
      ${this.open ? this.renderPanel() : nothing}
      ${this.toast
        ? html`<div class="note-toast" role="status">
            <span class="note-who ${this.toast.actor_name ? '' : 'system'}">
              ${initials(this.toast.actor_name)}
            </span>
            <button class="note-toast-open" @click=${() => void this.openNotification(this.toast!)}>
              ${this.toast.title}
            </button>
            <button
              class="note-toast-x"
              aria-label="Dismiss"
              @click=${() => (this.toast = null)}
            >
              ✕
            </button>
          </div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'notification-bell': NotificationBell;
  }
}
